import { Suspense, lazy } from "react";
import { motion, MotionConfig } from "framer-motion";
import { Rocket, MessageSquare } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { MascotFallback } from "../components/MascotViewer/MascotFallback";
import { useInViewOnce } from "../../../hooks/useInViewOnce";
import mascoteAcenandoUrl from "../../../assets/mascote3d/MascoteAcenando.glb?url";
import neurinhoFinalSvg from "../../../assets/mascote3d/NeurinhoFinalLandpage.svg";

const LazyMascotViewer = lazy(() =>
  import("../components/MascotViewer").then((m) => ({
    default: m.MascotViewer,
  }))
);

const SPRING: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

interface FinalCtaProps {
  onDemoCta: () => void;
  onLogin: () => void;
}

export function FinalCta({ onDemoCta, onLogin }: FinalCtaProps) {
  const { ref, isVisible } = useInViewOnce();

  return (
    <MotionConfig reducedMotion="user">
      <section
        ref={ref}
        className="border-b border-white/[0.06] py-16 sm:py-20 lg:py-24"
      >
        <div className="container mx-auto max-w-4xl px-4 sm:px-6">
          {/* Outer wrapper — allows mascote to overflow the card */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={isVisible ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.7, ease: SPRING }}
              className="relative overflow-hidden rounded-3xl border border-teal-400/25 bg-gradient-to-br from-teal-950/80 via-slate-900/90 to-slate-950/95 p-8 text-center backdrop-blur-xl sm:p-12 lg:p-16"
            >
              {/* Glow orbs */}
              <div
                aria-hidden
                className="pointer-events-none absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-teal-400/10 blur-3xl"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-16 right-1/4 h-48 w-48 rounded-full bg-cyan-500/10 blur-2xl"
              />

              <div className="relative z-10">
                <span className="badge-glow mb-6 inline-flex items-center gap-2 rounded-full border border-teal-400/30 bg-teal-500/10 px-4 py-1.5 text-sm font-medium text-teal-300">
                  <Rocket className="h-4 w-4" aria-hidden />
                  Pronto para começar?
                </span>

                <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl lg:text-5xl">
                  Leve o NeuroMent para{" "}
                  <span className="bg-gradient-to-r from-teal-300 via-cyan-200 to-sky-300 bg-clip-text text-transparent">
                    sua escola
                  </span>
                </h2>

                <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                  Solicite uma demonstração sem compromisso. Nosso time avalia o porte da
                  escola, os módulos ideais e retorna com proposta personalizada em até 2
                  dias úteis.
                </p>

                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:mt-10 sm:flex-row">
                  <Button
                    size="lg"
                    onClick={onDemoCta}
                    className="landing-cta-glow h-14 min-w-[200px] bg-gradient-to-r from-teal-500 to-cyan-600 px-8 text-base font-semibold hover:from-teal-400 hover:to-cyan-500"
                  >
                    <Rocket className="mr-2 h-5 w-5" aria-hidden />
                    Solicitar demonstração
                  </Button>
                  <Button
                    size="lg"
                    variant="ghost"
                    onClick={onLogin}
                    className="h-14 min-w-[160px] border border-white/20 bg-white/5 px-8 text-base text-white hover:border-white/35 hover:bg-white/10"
                  >
                    <MessageSquare className="mr-2 h-5 w-5" aria-hidden />
                    Já tenho conta
                  </Button>
                </div>

                <p className="mt-6 text-xs text-slate-500">
                  Sem cartão de crédito · Resposta em até 2 dias úteis · Sem plano gratuito de marketing
                </p>
              </div>
            </motion.div>

            {/* Mascote — desktop: canto inferior direito, levemente saindo do card */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={isVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.65, delay: 0.4, ease: SPRING }}
              className="pointer-events-none absolute -bottom-6 -right-6 hidden lg:block xl:-right-4"
              aria-hidden
            >
              <Suspense fallback={<MascotFallback animationType="wave" size={280} />}>
                <LazyMascotViewer
                  glbUrl={mascoteAcenandoUrl}
                  size={250}
                  rotationY={4.65}
                />
              </Suspense>
            </motion.div>

            {/* Mascote — tablet e mobile: SVG leve, sem WebGL */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={isVisible ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.65, delay: 0.4, ease: SPRING }}
              className="pointer-events-none absolute -bottom-4 -right-4 block lg:hidden"
              aria-hidden
            >
              <img
                src={neurinhoFinalSvg}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                className="w-[180px] h-[180px] object-contain will-change-transform drop-shadow-[0_0_32px_rgba(45,212,191,0.3)]"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </MotionConfig>
  );
}
